import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { hitosAPI, proyectosAPI, subdivisionesAPI } from '../utils/api';
import { useBreadcrumbs } from '../context/BreadcrumbContext';
import { toast } from 'sonner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { ArrowLeft, Plus, Edit, Trash2, Loader2, Save, Calculator, ArrowRight, ToggleLeft, ToggleRight, Mail } from 'lucide-react';
import { Badge } from '../components/ui/badge';

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr.includes('T') ? dateStr : dateStr + 'T00:00:00');
    return d.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return dateStr; }
};

const EstadoBadge = ({ activo }) => (
  <Badge className={`text-[10px] ${activo ? 'bg-green-100 text-green-700 hover:bg-green-100' : 'bg-red-100 text-red-700 hover:bg-red-100'}`}>
    {activo ? 'Activo' : 'Inactivo'}
  </Badge>
);

const EstadoEjecucionBadge = ({ estado }) => {
  const styles = {
    'Pendiente': 'bg-amber-50 text-amber-700 border-amber-200',
    'En proceso': 'bg-blue-50 text-blue-700 border-blue-200',
    'Ejecutado': 'bg-green-50 text-green-700 border-green-200',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border ${styles[estado] || styles['Pendiente']}`} data-testid={`estado-ejecucion-${estado}`}>
      {estado || 'Pendiente'}
    </span>
  );
};

const HitoDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { setBreadcrumbs } = useBreadcrumbs();
  const isNew = id === 'nuevo';

  // Start with loading=false; only set true when loading existing hito
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [codigo, setCodigo] = useState('');
  const [nombre, setNombre] = useState('');
  const [correosNotificacion, setCorreosNotificacion] = useState('');
  const [detalles, setDetalles] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [subdivisionesCache, setSubdivisionesCache] = useState({});
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [editingDetalle, setEditingDetalle] = useState(null);

  // Set breadcrumbs based on mode
  useEffect(() => {
    if (id === 'nuevo') {
      setBreadcrumbs([
        { label: 'G. de Proyectos', path: '#' },
        { label: 'Hitos', path: '/hitos' },
        { label: 'Nuevo', path: '#' },
      ]);
    }
    return () => setBreadcrumbs(null);
  }, [id, setBreadcrumbs]);

  useEffect(() => { loadProyectos(); }, []);

  useEffect(() => {
    if (id && id !== 'nuevo') {
      setLoading(true);
      loadHito();
    }
  }, [id]);

  const loadProyectos = async () => {
    try {
      const resp = await proyectosAPI.getAll();
      setProyectos(resp.data || []);
    } catch { /* silent */ }
  };

  const loadHito = async () => {
    try {
      const resp = await hitosAPI.getById(id);
      const data = resp.data;
      setCodigo(data.codigo || '');
      setNombre(data.nombre || '');
      setCorreosNotificacion(data.correos_notificacion || '');
      setDetalles(data.detalles || []);
      setBreadcrumbs([
        { label: 'G. de Proyectos', path: '#' },
        { label: 'Hitos', path: '/hitos' },
        { label: data.codigo, path: '#' },
      ]);
    } catch {
      toast.error('Error al cargar hito');
      navigate('/hitos');
    } finally {
      setLoading(false);
    }
  };

  const loadSubdivisiones = useCallback(async (proyectoId) => {
    if (subdivisionesCache[proyectoId]) return subdivisionesCache[proyectoId];
    try {
      const resp = await subdivisionesAPI.getByProyecto(proyectoId);
      const subs = resp.data || [];
      setSubdivisionesCache(prev => ({ ...prev, [proyectoId]: subs }));
      return subs;
    } catch { return []; }
  }, [subdivisionesCache]);

  const handleSaveHeader = async () => {
    if (!codigo.trim()) { toast.error('El código es obligatorio'); return; }
    if (!nombre.trim()) { toast.error('El nombre es obligatorio'); return; }

    if (isNew) {
      // For new hitos, need at least 1 detail
      if (detalles.length === 0) {
        toast.error('Debe agregar al menos una línea de detalle');
        return;
      }
      setSaving(true);
      try {
        const payload = {
          codigo: codigo.trim(),
          nombre: nombre.trim(),
          correos_notificacion: correosNotificacion.trim() || null,
          detalles: detalles.map(d => ({
            proyecto_id: d.proyecto_id,
            subdivision_id: d.subdivision_id || null,
            fecha_cumplimiento: d.fecha_cumplimiento,
          })),
        };
        const resp = await hitosAPI.create(payload);
        toast.success('Hito creado');
        navigate(`/hitos/${resp.data.id}`);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Error al crear');
      } finally {
        setSaving(false);
      }
    } else {
      setSaving(true);
      try {
        await hitosAPI.update(id, { codigo: codigo.trim(), nombre: nombre.trim(), correos_notificacion: correosNotificacion.trim() || null });
        toast.success('Cabecera actualizada');
        loadHito();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Error al actualizar');
      } finally {
        setSaving(false);
      }
    }
  };

  const handleAddDetalle = () => {
    setEditingDetalle(null);
    setShowDetailDialog(true);
  };

  const handleEditDetalle = (det) => {
    setEditingDetalle(det);
    setShowDetailDialog(true);
  };

  const handleDeleteDetalle = async (detalleId) => {
    if (!window.confirm('¿Eliminar esta línea de detalle?')) return;
    if (isNew) {
      setDetalles(prev => prev.filter(d => d._tempId !== detalleId));
    } else {
      try {
        await hitosAPI.deleteDetalle(detalleId);
        toast.success('Detalle eliminado');
        loadHito();
      } catch { toast.error('Error al eliminar'); }
    }
  };

  const handleToggleDetalle = async (detalleId) => {
    try {
      const resp = await hitosAPI.toggleDetalle(detalleId);
      toast.success(resp.data.activo ? 'Detalle activado' : 'Detalle desactivado');
      loadHito();
    } catch { toast.error('Error al cambiar estado'); }
  };

  const handleSaveDetalle = async (formData) => {
    if (isNew) {
      // For new hito, just add to local state
      const proj = proyectos.find(p => p.id === formData.proyecto_id);
      const subs = subdivisionesCache[formData.proyecto_id] || [];
      const sub = subs.find(s => s.id === formData.subdivision_id);
      const newDet = {
        ...formData,
        _tempId: Date.now().toString(),
        activo: true,
        created_at: new Date().toISOString(),
        proyecto_info: proj ? { nombre: proj.nombre, codigo_proyecto: proj.codigo_proyecto, tiene_subdivision: proj.tiene_subdivision, fecha_entrega: proj.fecha_entrega } : null,
        subdivision_info: sub ? { nombre: sub.nombre, codigo: sub.codigo, fecha_entrega: sub.fecha_entrega } : null,
      };
      if (editingDetalle) {
        setDetalles(prev => prev.map(d => d._tempId === editingDetalle._tempId ? { ...newDet, _tempId: editingDetalle._tempId } : d));
      } else {
        setDetalles(prev => [newDet, ...prev]);
      }
      setShowDetailDialog(false);
    } else {
      try {
        if (editingDetalle) {
          await hitosAPI.updateDetalle(editingDetalle.id, formData);
          toast.success('Detalle actualizado');
        } else {
          await hitosAPI.addDetalle(id, formData);
          toast.success('Detalle agregado');
        }
        setShowDetailDialog(false);
        loadHito();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Error al guardar detalle');
      }
    }
  };

  const getProjectName = (det) => {
    const info = det.proyecto_info;
    if (info) return `${info.nombre}${info.codigo_proyecto ? ` (${info.codigo_proyecto})` : ''}`;
    const p = proyectos.find(p => p.id === det.proyecto_id);
    return p ? `${p.nombre}${p.codigo_proyecto ? ` (${p.codigo_proyecto})` : ''}` : det.proyecto_id;
  };

  const getEtapaName = (det) => {
    if (!det.subdivision_id) return '—';
    const info = det.subdivision_info;
    if (info) return info.nombre;
    return det.subdivision_id;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={36} className="animate-spin text-[#9d8272]" />
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
    <div className="space-y-6">
      {/* Back button */}
      <Button variant="ghost" onClick={() => navigate('/hitos')} className="text-[#5A5A5A] hover:text-[#2D2D2D] -ml-2" data-testid="volver-hitos-btn">
        <ArrowLeft size={18} className="mr-2" />
        Volver a Hitos
      </Button>

      {/* CABECERA */}
      <div className="bg-white border border-[#E8E8E7] rounded-lg p-4 sm:p-6" data-testid="hito-cabecera">
        <h2 className="text-lg font-semibold text-[#9d8272] mb-3">Información General del Hito</h2>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
          <div>
            <Label className="text-xs">Código *</Label>
            <Input
              value={codigo}
              onChange={e => setCodigo(e.target.value.slice(0, 20))}
              className="border-[#c5b8a2] mt-1 h-9 text-sm font-mono"
              maxLength={20}
              placeholder="Ej: H1"
              data-testid="hito-codigo-input"
            />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Nombre *</Label>
            <Input
              value={nombre}
              onChange={e => setNombre(e.target.value.slice(0, 100))}
              className="border-[#c5b8a2] mt-1 h-9 text-sm"
              maxLength={100}
              placeholder="Ej: Licencia de Construcción"
              data-testid="hito-nombre-input"
            />
          </div>
          <Button
            onClick={handleSaveHeader}
            disabled={saving}
            className="bg-[#9d8272] hover:bg-[#8a7162] text-white h-9"
            data-testid="hito-guardar-btn"
          >
            {saving ? <Loader2 size={16} className="animate-spin mr-1" /> : <Save size={16} className="mr-1" />}
            {isNew ? 'Crear' : 'Guardar'}
          </Button>
        </div>
        <div className="mt-3">
          <Label className="text-xs flex items-center gap-1.5">
            <Mail size={12} className="text-[#9d8272]" />
            Correos para notificación
          </Label>
          <Input
            value={correosNotificacion}
            onChange={e => setCorreosNotificacion(e.target.value)}
            className="border-[#c5b8a2] mt-1 h-9 text-sm"
            placeholder="correo1@empresa.com, correo2@empresa.com"
            data-testid="hito-correos-input"
          />
          <p className="text-[11px] text-gray-400 mt-1">Lista de correos separados por comas. Se les notificará el cumplimiento de este hito.</p>
        </div>
      </div>

      {/* DETALLE */}
      <div className="bg-white border border-[#E8E8E7] shadow-sm rounded-lg" data-testid="hito-detalle-section">
        <div className="flex items-center justify-between px-4 sm:px-6 py-4">
          <h2 className="text-lg font-semibold text-[#9d8272]">Proyectos aplicados ({detalles.filter(d => d.activo).length})</h2>
          <Button size="sm" onClick={handleAddDetalle} className="h-8 bg-[#9d8272] hover:bg-[#8a7162] text-white" data-testid="agregar-detalle-btn">
            <Plus size={14} className="mr-1" /> Agregar
          </Button>
        </div>

        <div className="px-4 sm:px-6 pb-12 pt-2">

        {detalles.length === 0 ? (
          <div className="text-center py-8 bg-[#F5F5F5] rounded-lg">
            <p className="text-sm text-[#888888]">Sin líneas de detalle. Haga clic en "Agregar".</p>
            {isNew && <p className="text-xs text-amber-600 mt-1">Se requiere al menos 1 detalle para guardar.</p>}
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto" data-testid="detalle-table">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E8E8E7] text-left text-sm">
                    <th className="pb-2 font-semibold text-[#5A5A5A]">Proyecto</th>
                    <th className="pb-2 font-semibold text-[#5A5A5A]">Etapa</th>
                    <th className="pb-2 font-semibold text-[#5A5A5A]">Fecha Cumpl.</th>
                    <th className="pb-2 font-semibold text-[#5A5A5A]">Fecha</th>
                    <th className="pb-2 font-semibold text-[#5A5A5A]">Estado</th>
                    <th className="pb-2 font-semibold text-[#5A5A5A]">Ejecución</th>
                    <th className="pb-2 font-semibold text-[#5A5A5A] text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {detalles.map((det) => (
                    <tr key={det.id || det._tempId} className="border-b border-[#E8E8E7] hover:bg-[#F5F5F5] transition-colors text-sm h-12" data-testid={`detalle-row-${det.id || det._tempId}`}>
                      <td className="py-2 text-[#2D2D2D] font-medium align-middle">
                        <div className="truncate max-w-[200px]" title={getProjectName(det)}>{getProjectName(det)}</div>
                      </td>
                      <td className="py-2 text-[#5A5A5A] align-middle">{getEtapaName(det)}</td>
                      <td className="py-2 text-[#2D2D2D] font-medium align-middle">{formatDate(det.fecha_cumplimiento)}</td>
                      <td className="py-2 text-[#888888] text-xs align-middle whitespace-nowrap">{formatDate(det.created_at)}</td>
                      <td className="py-2 align-middle"><EstadoBadge activo={det.activo} /></td>
                      <td className="py-2 align-middle"><EstadoEjecucionBadge estado={det.estado_ejecucion} /></td>
                      <td className="py-2 align-middle">
                        <div className="flex items-center justify-center gap-1">
                          <Tooltip><TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleEditDetalle(det)} data-testid={`editar-detalle-${det.id || det._tempId}`}>
                            <Edit size={13} className="text-[#9d8272]" />
                          </Button>
                          </TooltipTrigger><TooltipContent>Editar detalle</TooltipContent></Tooltip>
                          {!isNew && (
                            <Tooltip><TooltipTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleToggleDetalle(det.id)} data-testid={`toggle-detalle-${det.id}`}>
                              {det.activo ? <ToggleRight size={15} className="text-green-600" /> : <ToggleLeft size={15} className="text-[#888888]" />}
                            </Button>
                            </TooltipTrigger><TooltipContent>{det.activo ? 'Desactivar' : 'Activar'}</TooltipContent></Tooltip>
                          )}
                          <Tooltip><TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => handleDeleteDetalle(det.id || det._tempId)} data-testid={`eliminar-detalle-${det.id || det._tempId}`}>
                            <Trash2 size={13} />
                          </Button>
                          </TooltipTrigger><TooltipContent>Eliminar detalle</TooltipContent></Tooltip>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-2" data-testid="detalle-cards">
              {detalles.map((det) => (
                <div key={det.id || det._tempId} className="border border-[#E8E8E7] rounded-lg p-3 bg-[#faf8f5]">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-[#2D2D2D] truncate">{getProjectName(det)}</p>
                      {det.subdivision_id && <p className="text-xs text-[#5A5A5A]">Etapa: {getEtapaName(det)}</p>}
                    </div>
                    <EstadoBadge activo={det.activo} />
                    <EstadoEjecucionBadge estado={det.estado_ejecucion} />
                  </div>
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#E8E8E7]">
                    <div>
                      <p className="text-xs text-[#888888]">Cumplimiento: <span className="font-medium text-[#2D2D2D]">{formatDate(det.fecha_cumplimiento)}</span></p>
                      <p className="text-[10px] text-[#aaa]">{formatDate(det.created_at)}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleEditDetalle(det)}>
                        <Edit size={12} className="text-[#9d8272]" />
                      </Button>
                      {!isNew && (
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleToggleDetalle(det.id)}>
                          {det.activo ? <ToggleRight size={14} className="text-green-600" /> : <ToggleLeft size={14} />}
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDeleteDetalle(det.id || det._tempId)}>
                        <Trash2 size={12} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        </div>
      </div>

      {/* Detail Dialog */}
      <DetalleDialog
        open={showDetailDialog}
        onOpenChange={setShowDetailDialog}
        editing={editingDetalle}
        proyectos={proyectos}
        subdivisionesCache={subdivisionesCache}
        loadSubdivisiones={loadSubdivisiones}
        onSave={handleSaveDetalle}
      />
    </div>
    </TooltipProvider>
  );
};

// ============ Detail Add/Edit Dialog ============
const DetalleDialog = ({ open, onOpenChange, editing, proyectos, subdivisionesCache, loadSubdivisiones, onSave }) => {
  const [proyectoId, setProyectoId] = useState('');
  const [subdivisionId, setSubdivisionId] = useState('');
  const [fechaCumplimiento, setFechaCumplimiento] = useState('');
  const [projectSearch, setProjectSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [showCalc, setShowCalc] = useState(false);
  const [calcMonths, setCalcMonths] = useState(0);
  const [calcOp, setCalcOp] = useState('add');
  const [saving, setSaving] = useState(false);
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);

  const selectedProject = proyectos.find(p => p.id === proyectoId);
  const parentSubs = selectedProject?.tiene_subdivision
    ? (subdivisionesCache[selectedProject.id] || []).filter(s => !s.padre_id)
    : [];

  useEffect(() => {
    if (open) {
      if (editing) {
        setProyectoId(editing.proyecto_id || '');
        setSubdivisionId(editing.subdivision_id || '');
        setFechaCumplimiento(editing.fecha_cumplimiento || '');
        const proj = proyectos.find(p => p.id === editing.proyecto_id);
        setProjectSearch(proj ? proj.nombre : (editing.proyecto_info?.nombre || ''));
        if (editing.proyecto_id && proj?.tiene_subdivision) {
          loadSubdivisiones(editing.proyecto_id);
        }
      } else {
        setProyectoId('');
        setSubdivisionId('');
        setFechaCumplimiento('');
        setProjectSearch('');
      }
      setShowCalc(false);
      setCalcMonths(0);
      setCalcOp('add');
    }
  }, [open, editing]);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleProjectSearch = (value) => {
    setProjectSearch(value);
    const q = value.toLowerCase();
    setFilteredProjects(
      value.trim()
        ? proyectos.filter(p => p.nombre.toLowerCase().includes(q) || (p.codigo_proyecto || '').toLowerCase().includes(q))
        : proyectos
    );
    setSearchOpen(true);
  };

  const selectProject = async (project) => {
    setProyectoId(project.id);
    setProjectSearch(project.nombre);
    setSubdivisionId('');
    setSearchOpen(false);
    if (project.tiene_subdivision) {
      await loadSubdivisiones(project.id);
    }
  };

  const getBaseFecha = () => {
    if (subdivisionId) {
      const subs = subdivisionesCache[proyectoId] || [];
      const sub = subs.find(s => s.id === subdivisionId);
      return sub?.fecha_entrega || null;
    }
    return selectedProject?.fecha_entrega || null;
  };

  const calcResult = () => {
    const base = getBaseFecha();
    if (!base || !calcMonths) return null;
    const d = new Date(base + 'T00:00:00');
    const months = parseInt(calcMonths) || 0;
    d.setMonth(d.getMonth() + (calcOp === 'add' ? months : -months));
    return d.toISOString().split('T')[0];
  };

  const applyCalcDate = () => {
    const result = calcResult();
    if (result) {
      setFechaCumplimiento(result);
      setShowCalc(false);
    }
  };

  const handleSubmit = async () => {
    if (!proyectoId) { toast.error('Seleccione un proyecto'); return; }
    if (!fechaCumplimiento) { toast.error('Ingrese la fecha de cumplimiento'); return; }
    setSaving(true);
    try {
      await onSave({
        proyecto_id: proyectoId,
        subdivision_id: subdivisionId || null,
        fecha_cumplimiento: fechaCumplimiento,
      });
    } finally {
      setSaving(false);
    }
  };

  const baseFecha = getBaseFecha();
  const calculated = calcResult();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg w-[95vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold text-[#2D2D2D]">
            {editing ? 'Editar Detalle' : 'Agregar Detalle'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-3">
          {/* Proyecto combobox */}
          <div className="relative">
            <Label className="text-xs">Proyecto *</Label>
            <Input
              ref={searchRef}
              value={projectSearch}
              onChange={e => handleProjectSearch(e.target.value)}
              onFocus={() => { setFilteredProjects(proyectos); setSearchOpen(true); }}
              className="border-[#c5b8a2] mt-1 h-9 text-sm"
              placeholder="Buscar proyecto..."
              data-testid="detalle-proyecto-input"
            />
            {searchOpen && (
              <div ref={dropdownRef} className="absolute z-50 w-full mt-1 bg-white border border-[#E8E8E7] rounded-md shadow-lg max-h-48 overflow-y-auto">
                {(filteredProjects.length > 0 ? filteredProjects : proyectos).map(p => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => selectProject(p)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-[#F5F5F5] flex items-center gap-2"
                    data-testid={`proyecto-option-${p.id}`}
                  >
                    <span className="truncate">{p.nombre}</span>
                    {p.codigo_proyecto && <Badge variant="outline" className="text-[9px] flex-shrink-0">{p.codigo_proyecto}</Badge>}
                  </button>
                ))}
                {filteredProjects.length === 0 && projectSearch.trim() && (
                  <p className="px-3 py-2 text-xs text-[#888888]">Sin resultados</p>
                )}
              </div>
            )}
          </div>

          {/* Etapa */}
          <div>
            <Label className="text-xs">Etapa</Label>
            <Select
              value={subdivisionId || "none"}
              onValueChange={v => setSubdivisionId(v === "none" ? "" : v)}
              disabled={!selectedProject?.tiene_subdivision}
            >
              <SelectTrigger className={`border-[#c5b8a2] mt-1 h-9 text-sm ${!selectedProject?.tiene_subdivision ? 'opacity-50' : ''}`} data-testid="detalle-etapa-select">
                <SelectValue placeholder={!selectedProject?.tiene_subdivision ? 'Sin subdivisiones' : 'Seleccione etapa'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Ninguna</SelectItem>
                {parentSubs.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Fecha Cumplimiento + Calculator */}
          <div>
            <Label className="text-xs">Fecha de Cumplimiento *</Label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                type="date"
                value={fechaCumplimiento}
                onChange={e => setFechaCumplimiento(e.target.value)}
                className="border-[#c5b8a2] h-9 text-sm flex-1"
                data-testid="detalle-fecha-input"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowCalc(!showCalc)}
                className={`h-9 px-2 flex-shrink-0 ${showCalc ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-[#c5b8a2]'}`}
                title="Calculadora de fecha"
                data-testid="calc-toggle"
              >
                <Calculator size={16} />
              </Button>
            </div>

            {showCalc && (
              <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-2" data-testid="calc-panel">
                <p className="text-xs text-blue-700 font-medium">Calcular desde Fecha de Entrega</p>
                {baseFecha ? (
                  <>
                    <div className="text-xs text-blue-600">
                      Base: <span className="font-semibold">{formatDate(baseFecha)}</span>
                      <span className="text-blue-400 ml-1">({subdivisionId ? 'de etapa' : 'del proyecto'})</span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Select value={calcOp} onValueChange={v => setCalcOp(v)}>
                        <SelectTrigger className="w-20 h-8 text-xs border-blue-200 bg-white"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="add">+ Sumar</SelectItem>
                          <SelectItem value="sub">- Restar</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input type="number" min="0" max="120" value={calcMonths} onChange={e => setCalcMonths(e.target.value)} className="w-16 h-8 text-xs border-blue-200 bg-white text-center" data-testid="calc-months" />
                      <span className="text-xs text-blue-600">meses</span>
                      {calculated && (
                        <>
                          <ArrowRight size={14} className="text-blue-400" />
                          <span className="text-xs font-semibold text-blue-800">{formatDate(calculated)}</span>
                        </>
                      )}
                    </div>
                    {calculated && (
                      <Button type="button" size="sm" onClick={applyCalcDate} className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white" data-testid="calc-apply">
                        Aplicar
                      </Button>
                    )}
                  </>
                ) : (
                  <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                    {proyectoId ? 'El proyecto/etapa no tiene fecha de entrega. Ingrese manualmente.' : 'Seleccione un proyecto primero.'}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3 pt-3 border-t border-[#E8E8E7]">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="detalle-cancelar-btn">Cancelar</Button>
            <Button onClick={handleSubmit} disabled={saving} className="bg-[#9d8272] hover:bg-[#8a7162] text-white" data-testid="detalle-guardar-btn">
              {saving ? 'Guardando...' : editing ? 'Actualizar' : 'Agregar'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default HitoDetalle;
